import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewChild} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatButtonModule} from '@angular/material/button';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatIconModule} from '@angular/material/icon';
import {MatProgressBarModule} from '@angular/material/progress-bar';
import {MatChipsModule} from '@angular/material/chips';
import {MatCardModule} from '@angular/material/card';
import {MatSnackBar, MatSnackBarModule} from '@angular/material/snack-bar';
import {animate, style, transition, trigger} from '@angular/animations';
import {ActivatedRoute} from '@angular/router';
import {RestFactoryService} from '../../../core/services/rest-factory.service';
import {finalize, take} from 'rxjs';
import {
    ApexAxisChartSeries,
    ApexChart,
    ApexDataLabels,
    ApexGrid,
    ApexLegend,
    ApexStroke,
    ApexTheme,
    ApexTooltip,
    ApexXAxis,
    ApexYAxis,
    ChartComponent,
    NgApexchartsModule
} from "ng-apexcharts";
import {MatTooltip} from "@angular/material/tooltip";

export type ChartOptions = {
    series: ApexAxisChartSeries;
    chart: ApexChart;
    xaxis: ApexXAxis;
    stroke: ApexStroke;
    tooltip: ApexTooltip;
    dataLabels: ApexDataLabels;
    yaxis: ApexYAxis;
    legend: ApexLegend;
    grid: ApexGrid;
    colors: string[];
    theme: ApexTheme;
};

@Component({
    selector: 'app-ambassador-stats',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        MatProgressBarModule,
        MatChipsModule,
        MatCardModule,
        MatSnackBarModule,
        NgApexchartsModule,
        MatTooltip
    ],
    templateUrl: './ambassador-stats.component.html',
    styleUrls: ['./ambassador-stats.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('fadeIn', [
            transition(':enter', [
                style({ opacity: 0, transform: 'translateY(10px)' }),
                animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
            ]),
        ]),
    ],
})
export class AmbassadorStatsComponent implements OnInit {
    form: FormGroup;
    isLoading = false;
    showStats = false;

    savedCodes: string[] = [];
    currentCode: string = '';

    // Data Holders
    statsData: any = null;
    totalSignups: number = 0;
    totalStandard: number = 0; // New
    totalSubs: number = 0;     // New
    bestWeek: number = 0;
    currentRank: string = '-';
    title: string = '';

    // Charts
    @ViewChild("chart") chart: ChartComponent;
    public chartOptions: Partial<ChartOptions>;

    // New Rank Chart
    public rankChartOptions: Partial<ChartOptions>;

    private readonly API_ENDPOINT = '/admin/dashboard/metrics/membersleaderboard';
    private readonly STORAGE_KEY = 'ambassador_codes_history';

    constructor(
        private _fb: FormBuilder,
        private _restFactoryService: RestFactoryService,
        private _cd: ChangeDetectorRef,
        private _snackBar: MatSnackBar,
        private _route: ActivatedRoute
    ) {}

    ngOnInit(): void {
        this.form = this._fb.group({
            ds_leaderboardkey: ['', [Validators.required, Validators.minLength(3)]]
        });
        this.loadSavedCodes();

        this._route.queryParams.pipe(take(1)).subscribe(params => {
            const key = params['key'];
            if (key) {
                this.form.patchValue({ ds_leaderboardkey: key });
                if (this.form.valid) {
                    this.fetchStats();
                }
            }
        });
    }

    loadSavedCodes(): void {
        const stored = localStorage.getItem(this.STORAGE_KEY);
        if (stored) {
            try { this.savedCodes = JSON.parse(stored); } catch (e) { this.savedCodes = []; }
        }
    }

    saveCodeToHistory(code: string): void {
        const upperCode = code.toUpperCase().trim();
        this.savedCodes = this.savedCodes.filter(c => c !== upperCode);
        this.savedCodes.unshift(upperCode);
        if (this.savedCodes.length > 10) this.savedCodes.pop();
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.savedCodes));
    }

    removeCodeFromHistory(code: string, event: Event): void {
        event.stopPropagation();
        this.savedCodes = this.savedCodes.filter(c => c !== code);
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.savedCodes));
        this._cd.markForCheck();
    }

    selectCode(code: string): void {
        this.form.patchValue({ ds_leaderboardkey: code });
        this.fetchStats();
    }

    resetView(): void {
        this.showStats = false;
        this.statsData = null;
        this.currentCode = '';
        this.form.reset();
        this.loadSavedCodes();
        this._cd.markForCheck();
    }

    fetchStats(): void {
        if (this.form.invalid) return;
        const code = this.form.get('ds_leaderboardkey')?.value?.trim().toUpperCase();
        if (!code) return;

        this.isLoading = true;
        this.currentCode = code;
        this.form.disable();

        const payload = new FormData();
        payload.append('ds_leaderboardkey', code);

        this._restFactoryService.httpPostService(this.API_ENDPOINT, payload)
            .pipe(
                take(1),
                finalize(() => {
                    this.isLoading = false;
                    this.form.enable();
                    this._cd.markForCheck();
                })
            )
            .subscribe({
                next: (response) => {
                    if (response && response.ds_Status === 'Ok') {
                        this.processData(response);
                        this.saveCodeToHistory(code);
                        this.showStats = true;
                    } else {
                        this._snackBar.open('Invalid response from server.', 'Close', { duration: 3000 });
                    }
                    this._cd.markForCheck();
                },
                error: (error) => {
                    console.error('Error', error);
                    this._snackBar.open('Could not find stats for this code.', 'Close', { duration: 3000 });
                }
            });
    }

    processData(data: any): void {
        this.statsData = data;

        this.title = data.ds_title || '';

        // --- 1. Calculate KPIs ---
        const weeklyData = data.ar_signupsperweek || [];
        this.totalSignups = 0;
        this.totalStandard = 0;
        this.totalSubs = 0;
        this.bestWeek = 0;

        const categories: string[] = [];
        const subSeries: number[] = [];
        const nonSubSeries: number[] = [];

        weeklyData.forEach((week: any) => {
            const total = parseInt(week.total_signups, 10) || 0;
            const subs = parseInt(week.subscription_signups, 10) || 0;
            const nonSubs = parseInt(week.non_subscription_signups, 10) || 0;

            this.totalSignups += total;
            this.totalStandard += nonSubs;
            this.totalSubs += subs;

            if (total > this.bestWeek) this.bestWeek = total;

            const date = new Date(week.week_start);
            const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            categories.push(dateStr);

            subSeries.push(subs);
            nonSubSeries.push(nonSubs);
        });

        // --- 2. Acquisition Chart ---
        const textColor = 'var(--fuse-text-default, #334155)';

        this.chartOptions = {
            series: [
                { name: "Subscriptions", data: subSeries },
                { name: "Standard Signups", data: nonSubSeries }
            ],
            chart: {
                type: "area",
                height: 350,
                toolbar: { show: false },
                fontFamily: 'inherit'
            },
            colors: ['#7E4798', '#94a3b8'], // <--- MODIFIED: Replaced '#6366f1' with theme main color #7E4798
            dataLabels: { enabled: false },
            stroke: { curve: "smooth", width: 3 },
            xaxis: {
                categories: categories,
                axisBorder: { show: false },
                axisTicks: { show: false },
                labels: { style: { colors: textColor } }
            },
            yaxis: {
                labels: {
                    formatter: (value) => value.toFixed(0),
                    style: { colors: textColor }
                }
            },
            tooltip: { theme: 'dark' },
            grid: {
                borderColor: 'var(--mat-sidenav-border-color, #e2e8f0)',
                strokeDashArray: 4
            },
            legend: {
                position: 'top',
                horizontalAlign: 'right',
                labels: { colors: textColor }
            }
        };

        // --- 3. Progressive Leaderboard Chart (Rank History) ---
        this.generateRankChart(data.ar_progresiveaderboard);
    }

    generateRankChart(progressiveData: any): void {
        if (!progressiveData) {
            this.rankChartOptions = null;
            return;
        }

        const textColor = 'var(--fuse-text-default, #334155)';

        // Extract and sort dates
        const dates = Object.keys(progressiveData).sort();

        // Format x-axis labels
        const dateLabels = dates.map(d => {
            const date = new Date(d);
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        });

        // Identify all unique participants across these weeks
        const participants = new Set<string>();
        dates.forEach(d => {
            if(Array.isArray(progressiveData[d])) {
                progressiveData[d].forEach((p: any) => participants.add(p.ds_title));
            }
        });

        // Build series for each participant
        const rankSeries: ApexAxisChartSeries = [];
        participants.forEach(name => {
            const dataPoints = dates.map(dateKey => {
                const weekList = progressiveData[dateKey] || [];
                const found = weekList.find((x: any) => x.ds_title === name);
                // Return rank if found, otherwise null (break line) or a low rank value
                return found ? parseInt(found.place, 10) : null;
            });

            // Optional: Filter out participants who only appear once or have very low ranks if too noisy
            // For now, we show all
            rankSeries.push({
                name: name,
                data: dataPoints
            });
        });

        this.rankChartOptions = {
            series: rankSeries,
            chart: {
                type: 'line',
                height: 350,
                toolbar: { show: false },
                fontFamily: 'inherit',
                animations: { enabled: true }
            },
            colors: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#6366F1', '#14B8A6'],
            stroke: {
                width: 3,
                curve: 'monotoneCubic' // Smoother lines for ranking changes
            },
            xaxis: {
                categories: dateLabels,
                axisBorder: { show: false },
                axisTicks: { show: false },
                labels: { style: { colors: textColor } }
            },
            yaxis: {
                reversed: true, // Rank 1 is at the top
                min: 1,
                max: 10, // Assuming top 10 list
                tickAmount: 9, // Intervals of 1
                labels: {
                    style: { colors: textColor },
                    formatter: (val) => `Rank #${val.toFixed(0)}`
                },
                title: {
                    text: 'Weekly Position',
                    style: { color: textColor }
                }
            },
            grid: {
                borderColor: 'var(--mat-sidenav-border-color, #e2e8f0)',
                strokeDashArray: 4
            },
            legend: {
                show: true,
                position: 'bottom',
                itemMargin: { horizontal: 10, vertical: 5 },
                labels: { colors: textColor }
            },
            tooltip: {
                theme: 'dark',
                y: {
                    formatter: (val) => val ? `#${val}` : 'N/A'
                }
            }
        };
    }
}
